import { gql, useLazyQuery } from "@apollo/client";
import { getErrors } from "../../utils";
import { AsyncQueryStatus } from "../../models/AsyncActionStatus";
import { MetricTrendModel, PermitMetricQueryInput } from "./getPermitsCountByStatesMetric";

// ---------------------------------------------------------------------------
// Shared response shape — both duration metrics return the flat MetricValueModel
// ---------------------------------------------------------------------------

export interface AvgDurationMetricValue {
  __typename: "MetricValueModel";
  value: number;
  previousValue: number | null;
  delta: number | null;
  deltaPercentage: number | null;
  trend: MetricTrendModel;
}

export interface AvgDurationMetricVariables {
  query: PermitMetricQueryInput;
}

// ---------------------------------------------------------------------------
// In-progress -> ready-to-issue average duration
// ---------------------------------------------------------------------------

export interface PermitInProgressAvgDurationMetric {
  permitInProgressAvgDurationMetric: AvgDurationMetricValue;
}

export const GET_PERMIT_IN_PROGRESS_AVG_DURATION_METRIC = gql`
  query PermitInProgressAvgDurationMetric($query: PermitMetricQueryInput!) {
    permitInProgressAvgDurationMetric(query: $query) {
      value
      previousValue
      delta
      deltaPercentage
      trend {
        key
        name
      }
    }
  }
`;

export function useGetPermitInProgressAvgDurationMetric() {
  const [getPermitInProgressAvgDurationMetric, { loading, error, data }] = useLazyQuery<
    PermitInProgressAvgDurationMetric,
    AvgDurationMetricVariables
  >(GET_PERMIT_IN_PROGRESS_AVG_DURATION_METRIC, {
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  return {
    getPermitInProgressAvgDurationMetric,
    data,
    status: {
      loading,
      errors: getErrors(error),
      successful: !error && data ? true : false,
    } as AsyncQueryStatus,
  };
}

// ---------------------------------------------------------------------------
// Expired -> completed average duration
// ---------------------------------------------------------------------------

export interface PermitExpiredAvgDurationMetric {
  permitExpiredAvgDurationMetric: AvgDurationMetricValue;
}

export const GET_PERMIT_EXPIRED_AVG_DURATION_METRIC = gql`
  query PermitExpiredAvgDurationMetric($query: PermitMetricQueryInput!) {
    permitExpiredAvgDurationMetric(query: $query) {
      value
      previousValue
      delta
      deltaPercentage
      trend {
        key
        name
      }
    }
  }
`;

export function useGetPermitExpiredAvgDurationMetric() {
  const [getPermitExpiredAvgDurationMetric, { loading, error, data }] = useLazyQuery<
    PermitExpiredAvgDurationMetric,
    AvgDurationMetricVariables
  >(GET_PERMIT_EXPIRED_AVG_DURATION_METRIC, {
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  return {
    getPermitExpiredAvgDurationMetric,
    data,
    status: {
      loading,
      errors: getErrors(error),
      successful: !error && data ? true : false,
    } as AsyncQueryStatus,
  };
}
